import { type FeatureDefinition, GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { css, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { DevelopmentQueryDevtools } from '@openx/components-core/development/DevelopmentQueryDevtools.tsx';
import { ConfirmationDialogProvider } from '@openx/components-core/lib/Dialog/ConfirmationDialog/ConfirmationDialogProvider.tsx';
import { SiteUnavailablePopup } from '@openx/components-core/lib/SiteUnavailablePopup/SiteUnavailablePopup.tsx';
import { SnackbarProvider, snakbarStyles } from '@openx/components-core/lib/Snackbar/SnackbarProvider.tsx';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import { createQueryClient } from '@openx/constants/react-query.ts';
import { onQueryError } from '@openx/utils/api/onQueryError.tsx';
import { createDefaultGrowthBook } from '@openx/utils/lib/createDefaultGrowthBook.ts';
import { sessionQueryKey } from '@openx/utils/session/sessionQueryOptions.ts';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { SnackbarProvider as ISnackbarProvider } from 'notistack';
import { useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import { http } from './api/index.ts';
import { AppRoutes } from './components/AppRoutes/AppRoutes.tsx';
import { ErrorPage } from './components/ErrorPage/ErrorPage.tsx';
import { DEAL_LIBRARY_VERSION, GROWTHBOOK_NAME_ATTR } from './config.ts';
import { ConfigProviders } from './configs/ConfigProviders.tsx';
import { environment } from './environments/environment.ts';
import { session as dealLibrarySessionStub } from './state/session/reducer.ts';
import { store } from './store.ts';

const globalStyles = css`
  * {
    font-family: 'Figtree', sans-serif;
    margin: 0;
    padding: 0;
  }

  a {
    text-decoration: none;
  }
`;

type GbFeatureValue = boolean | string | null;

const toGbFeatures = (values: Record<string, GbFeatureValue> | undefined): Record<string, FeatureDefinition> => {
  return Object.fromEntries(Object.entries(values ?? {}).map(([key, value]) => [key, { defaultValue: value }]));
};

export function App() {
  const snackbarRef = useRef<ISnackbarProvider>(null);

  const queryClient = useRef<QueryClient | undefined>(undefined);

  if (!queryClient.current) {
    queryClient.current = createQueryClient(http, onQueryError);
    // Deal Library has no auth/session. Seed an empty session so useSession()/useIsAllowed()
    // (shared with authenticated apps) resolve to "no permissions" instead of throwing.
    queryClient.current.setQueryData(sessionQueryKey, dealLibrarySessionStub());
  }

  const defaultGrowthBook = useRef(createDefaultGrowthBook());

  const staticFeatures = useMemo(() => toGbFeatures(environment.growthbookFeatures), []);

  const staticGrowthBook = useRef(
    new GrowthBook({
      attributes: { app: GROWTHBOOK_NAME_ATTR },
      features: staticFeatures,
    })
  );

  useEffect(() => {
    // biome-ignore lint/suspicious/noConsole: logging info in console
    console.info(`%cDeal Library version: ${DEAL_LIBRARY_VERSION}`, 'font-weight:bold;');
  }, []);

  return (
    <QueryClientProvider client={queryClient.current}>
      <DevelopmentQueryDevtools />
      <BrowserRouter useTransitions={false}>
        <GrowthBookProvider growthbook={staticGrowthBook.current || defaultGrowthBook.current}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <ConfigProviders>
                <SnackbarProvider ref={snackbarRef}>
                  <GlobalStyles styles={[globalStyles, snakbarStyles(theme)]} />
                  <ErrorBoundary fallbackRender={() => <ErrorPage />}>
                    <ConfirmationDialogProvider>
                      <Provider store={store}>
                        <SiteUnavailablePopup />
                        <AppRoutes />
                      </Provider>
                    </ConfirmationDialogProvider>
                  </ErrorBoundary>
                </SnackbarProvider>
              </ConfigProviders>
            </ThemeProvider>
          </StyledEngineProvider>
        </GrowthBookProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
